import { memo, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { Hash, Search as SearchIcon, Sparkles } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui/tooltip";
import { type MatchSource } from "~/hooks/search/match-sources";

const ICON_BY_SOURCE: Record<
  MatchSource,
  ComponentType<{ size?: number; "aria-hidden"?: boolean }>
> = {
  isbn: Hash,
  keyword: SearchIcon,
  semantic: Sparkles,
};

interface MatchTypeBadgesProps {
  sources: readonly MatchSource[];
}

export const MatchTypeBadges = memo(({ sources }: MatchTypeBadgesProps) => {
  const { t } = useTranslation(["books"]);

  if (sources.length === 0) return null;

  const labelBySource: Record<MatchSource, string> = {
    isbn: t("books:booktable.matchType.isbn"),
    keyword: t("books:booktable.matchType.metadata"),
    semantic: t("books:booktable.matchType.semantic"),
  };

  return (
    <div className="flex items-center gap-2">
      {sources.map((source) => {
        const Icon = ICON_BY_SOURCE[source];
        const label = labelBySource[source];
        return (
          <Tooltip key={source}>
            <TooltipTrigger asChild>
              {/** biome-ignore lint/a11y/useAriaPropsSupportedByRole: giving this element a role is a separate accessibility change */}
              <span
                data-testid={`match-badge-${source}`}
                aria-label={label}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-secondary-100 text-secondary-500"
              >
                <Icon size={14} aria-hidden />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">{label}</TooltipContent>
          </Tooltip>
        );
      })}
    </div>
  );
});

MatchTypeBadges.displayName = "MatchTypeBadges";
